/**
 * VoiceOverlay — Floating overlay showing real-time voice state feedback.
 *
 * Displayed when voice mode is active. Shows:
 *  recording  → pulsing mic animation + status text
 *  processing → spinner + "Thinking…"
 *  speaking   → sound wave animation + "Speaking…"
 */

import React from 'react';
import { Box, Typography, Fade } from '@mui/material';
import {
  Mic as MicIcon,
  VolumeUp as VolumeUpIcon,
  HourglassBottom as HourglassIcon,
} from '@mui/icons-material';
import type { VoiceState } from '../../hooks/useVoiceChat';

interface VoiceOverlayProps {
  voiceState: VoiceState;
  interimTranscript: string;
}

export default function VoiceOverlay({ voiceState, interimTranscript }: VoiceOverlayProps) {
  if (voiceState === 'idle') return null;

  const stateConfig: Record<Exclude<VoiceState, 'idle'>, { icon: React.ReactNode; label: string; color: string }> = {
    recording: {
      icon: <MicIcon sx={{ fontSize: 28 }} />,
      label: interimTranscript || 'Recording…',
      color: '#f44336',
    },
    processing: {
      icon: <HourglassIcon sx={{ fontSize: 28 }} />,
      label: 'Thinking…',
      color: '#ff9800',
    },
    speaking: {
      icon: <VolumeUpIcon sx={{ fontSize: 28 }} />,
      label: 'Speaking…',
      color: '#4caf50',
    },
  };

  const { icon, label, color } = stateConfig[voiceState as Exclude<VoiceState, 'idle'>];

  return (
    <Fade in>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 0.75,
          borderRadius: 2,
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: color,
          boxShadow: `0 0 8px ${color}40`,
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            color,
            display: 'flex',
            alignItems: 'center',
            animation: voiceState === 'recording' ? 'voicePulseIcon 1.5s infinite' : 'none',
            '@keyframes voicePulseIcon': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.4 },
            },
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="body2"
          noWrap
          sx={{
            color: 'text.secondary',
            fontStyle: voiceState === 'recording' && interimTranscript ? 'italic' : 'normal',
            maxWidth: 300,
          }}
        >
          {label}
        </Typography>
      </Box>
    </Fade>
  );
}
